import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { WorkflowDefinition } from "../../model/workflows/workflow-definition";
import { StepDefinition } from "../../model/workflows/step-definition";
import { ActionDefinition } from "../../model/workflows/action-definition";
import { SLADurationType } from "../../model/slaDurationType";
import { AssignType } from "../../model/workflows/asign-type";
import { Attribute } from "../../model/service-request/attribute";
import { SectionDefinition } from "../../model/service-request/section-definition";
import { ServiceRequestType } from "../../model/service-request/service-request";

const initialState: WorkflowDefinition = {
	id: "",
	name: "",
	description: "",
	creatorId: "",
	utilityId: "",
	createdAt: "",
	stepDefinitions: [],
	serviceRequest: undefined,
};

interface ChangeStepOrdersAction {
	steps: StepDefinition[];
}
interface RenameSectionAction {
	stepDefinition: StepDefinition;
	newName: string;
}

interface AddFieldToStepAction {
	stepId: string;
	sectionIndex: number;
	newAttribute: Attribute;
	existingAttribute?: Attribute;
}

interface EnableFieldInStepAction {
	stepId: string;
	sectionIndex: number;
	attribute: Attribute;
	enabled: boolean;
}

interface RemoveFieldFromStepAction {
	stepId: string;
	sectionIndex: number;
	attribute: Attribute;
}

interface ChangeFieldOrdersInStepAction {
	stepId: string;
	sectionIndex: number;
	attributes: Attribute[];
}

interface ChangeSectionOrdersInStepAction {
	stepId: string;
	sections: SectionDefinition[];
}

interface RenameSectionInStepAction {
	stepId: string;
	section: SectionDefinition;
	newName: string;
}

const workflowSlice = createSlice({
	name: "serviceType",
	initialState,
	reducers: {
		setWorkflow: (state, action: PayloadAction<WorkflowDefinition>) => {
			Object.assign(state, action.payload);
		},
		resetState: state => {
			Object.assign(state, initialState);
		},
		setWorkflowId: (state, action: PayloadAction<string>) => {
			state.id = action.payload;
		},
		setWorkflowName: (state, action: PayloadAction<string>) => {
			state.name = action.payload;
		},
		setWorkflowDescription: (state, action: PayloadAction<string>) => {
			state.description = action.payload;
		},
		addStep: (state, action: PayloadAction<StepDefinition>) => {
			state.stepDefinitions = [...state.stepDefinitions, action.payload];
		},
		removeStep: (state, action: PayloadAction<StepDefinition>) => {
			let attrIndex = state.stepDefinitions.findIndex(x => x.id == action.payload.id);
			state.stepDefinitions.splice(attrIndex, 1);
		},
		changeOrderOfSteps: (state, action: PayloadAction<ChangeStepOrdersAction>) => {
			const { steps } = action.payload;
			state.stepDefinitions = steps;
		},
		renameStep: (state, action: PayloadAction<RenameSectionAction>) => {
			var index = state.stepDefinitions.findIndex(x => x.id == action.payload.stepDefinition.id);
			state.stepDefinitions[index] = { ...state.stepDefinitions[index], name: action.payload.newName };
		},
		updateStep: (state, action: PayloadAction<StepDefinition>) => {
			const index = state.stepDefinitions.findIndex(x => x.id === action.payload.id);
			if (index !== -1) {
				state.stepDefinitions[index] = action.payload;
			}
		},
		addActionToStep: (state, action: PayloadAction<{ stepId: string; action: ActionDefinition }>) => {
			const stepIndex = state.stepDefinitions.findIndex(x => x.id === action.payload.stepId);
			if (stepIndex !== -1) {
				const step = state.stepDefinitions[stepIndex];
				if (!step.actions) {
					step.actions = [];
				}
				step.actions = [...step.actions, action.payload.action];
			}
		},
		removeActionFromStep: (state, action: PayloadAction<{ stepId: string; actionId: string }>) => {
			const stepIndex = state.stepDefinitions.findIndex(x => x.id === action.payload.stepId);
			if (stepIndex !== -1 && state.stepDefinitions[stepIndex].actions) {
				state.stepDefinitions[stepIndex].actions = state.stepDefinitions[stepIndex].actions!.filter(a => a.id !== action.payload.actionId);
			}
		},
		updateActionInStep: (state, action: PayloadAction<{ stepId: string; action: ActionDefinition }>) => {
			const stepIndex = state.stepDefinitions.findIndex(x => x.id === action.payload.stepId);
			if (stepIndex !== -1 && state.stepDefinitions[stepIndex].actions) {
				const actionIndex = state.stepDefinitions[stepIndex].actions!.findIndex(a => a.id === action.payload.action.id);
				if (actionIndex !== -1) {
					state.stepDefinitions[stepIndex].actions![actionIndex] = action.payload.action;
				}
			}
		},
		updateActionNextStep: (state, action: PayloadAction<{ stepId: string; actionId: string; nextStepId: string | null }>) => {
			const stepIndex = state.stepDefinitions.findIndex(x => x.id === action.payload.stepId);
			if (stepIndex !== -1 && state.stepDefinitions[stepIndex].actions) {
				const actionIndex = state.stepDefinitions[stepIndex].actions!.findIndex(a => a.id === action.payload.actionId);
				if (actionIndex !== -1) {
					state.stepDefinitions[stepIndex].actions![actionIndex].nextStepDefinitionId = action.payload.nextStepId;
				}
			}
		},
		updateStepPaymentRequired: (state, action: PayloadAction<{ stepId: string; paymentRequired: boolean }>) => {
			const stepIndex = state.stepDefinitions.findIndex(x => x.id === action.payload.stepId);
			if (stepIndex !== -1) {
				state.stepDefinitions[stepIndex].isPayRequired = action.payload.paymentRequired;
			}
		},
		updateStepSLADurationType: (state, action: PayloadAction<{ stepId: string; slaDurationType: SLADurationType }>) => {
			const stepIndex = state.stepDefinitions.findIndex(x => x.id === action.payload.stepId);
			if (stepIndex !== -1) {
				state.stepDefinitions[stepIndex].slaDurationType = action.payload.slaDurationType;
			}
		},
		updateStepSLADuration: (state, action: PayloadAction<{ stepId: string; slaDuration: number }>) => {
			const stepIndex = state.stepDefinitions.findIndex(x => x.id === action.payload.stepId);
			if (stepIndex !== -1) {
				state.stepDefinitions[stepIndex].slaDuration = action.payload.slaDuration;
			}
		},
		updateStepAssignType: (state, action: PayloadAction<{ stepId: string; assignType: AssignType }>) => {
			const stepIndex = state.stepDefinitions.findIndex(x => x.id === action.payload.stepId);
			if (stepIndex !== -1) {
				state.stepDefinitions[stepIndex].assignType = action.payload.assignType;
			}
		},
		updateStepAbleToSeeCustomerDetails: (state, action: PayloadAction<{ stepId: string; ableToSeeCustomerDetails: boolean }>) => {
			const stepIndex = state.stepDefinitions.findIndex(x => x.id === action.payload.stepId);
			if (stepIndex !== -1) {
				state.stepDefinitions[stepIndex].ableToSeeCustomerDetails = action.payload.ableToSeeCustomerDetails;
			}
		},
		updateStepAbleToSeeOtherStepInformation: (state, action: PayloadAction<{ stepId: string; ableToSeeOtherStepInformation: boolean }>) => {
			const stepIndex = state.stepDefinitions.findIndex(x => x.id === action.payload.stepId);
			if (stepIndex !== -1) {
				state.stepDefinitions[stepIndex].ableToSeeOtherStepInformation = action.payload.ableToSeeOtherStepInformation;
			}
		},
		updateStepAdditionalServiceRequest: (state, action: PayloadAction<{ stepId: string; additionalServiceRequest: ServiceRequestType  | null }>) => {
			const stepIndex = state.stepDefinitions.findIndex(x => x.id === action.payload.stepId);
			if (stepIndex !== -1) {
				state.stepDefinitions[stepIndex].additionalServiceRequest = action.payload.additionalServiceRequest;
			}
		},
		addFieldToStepAdditionalServiceRequest: (state, action: PayloadAction<AddFieldToStepAction>) => {
			const stepIndex = state.stepDefinitions.findIndex(x => x.id === action.payload.stepId);
			if (stepIndex !== -1 && state.stepDefinitions[stepIndex].additionalServiceRequest) {
				const serviceRequest = state.stepDefinitions[stepIndex].additionalServiceRequest!;
				const { newAttribute, existingAttribute, sectionIndex } = action.payload;

				if (existingAttribute) {
					const index = serviceRequest.sectionDefinitions[sectionIndex].attributeDefinitions.findIndex(x => x.id === existingAttribute.id);
					if (index !== -1) {
						serviceRequest.sectionDefinitions[sectionIndex].attributeDefinitions[index] = newAttribute;
					} else {
						serviceRequest.sectionDefinitions[sectionIndex].attributeDefinitions.push(newAttribute);
					}
				} else {
					serviceRequest.sectionDefinitions[sectionIndex].attributeDefinitions.push(newAttribute);
				}
			}
		},
		enableFieldInStepAdditionalServiceRequest: (state, action: PayloadAction<EnableFieldInStepAction>) => {
			const stepIndex = state.stepDefinitions.findIndex(x => x.id === action.payload.stepId);
			if (stepIndex !== -1 && state.stepDefinitions[stepIndex].additionalServiceRequest) {
				const serviceRequest = state.stepDefinitions[stepIndex].additionalServiceRequest!;
				const { attribute, enabled, sectionIndex } = action.payload;
				const index = serviceRequest.sectionDefinitions[sectionIndex].attributeDefinitions.findIndex(x => x.id === attribute.id);

				if (index !== -1) {
					serviceRequest.sectionDefinitions[sectionIndex].attributeDefinitions[index] = {
						...serviceRequest.sectionDefinitions[sectionIndex].attributeDefinitions[index],
						isEnabled: enabled,
					};
				}
			}
		},
		removeFieldFromStepAdditionalServiceRequest: (state, action: PayloadAction<RemoveFieldFromStepAction>) => {
			const stepIndex = state.stepDefinitions.findIndex(x => x.id === action.payload.stepId);
			if (stepIndex !== -1 && state.stepDefinitions[stepIndex].additionalServiceRequest) {
				const serviceRequest = state.stepDefinitions[stepIndex].additionalServiceRequest!;
				const { attribute, sectionIndex } = action.payload;
				const attrIndex = serviceRequest.sectionDefinitions[sectionIndex].attributeDefinitions.findIndex(x => x.id === attribute.id);
				if (attrIndex !== -1) {
					serviceRequest.sectionDefinitions[sectionIndex].attributeDefinitions.splice(attrIndex, 1);
				}
			}
		},
		changeOrderOfAttributesInStepAdditionalServiceRequest: (state, action: PayloadAction<ChangeFieldOrdersInStepAction>) => {
			const stepIndex = state.stepDefinitions.findIndex(x => x.id === action.payload.stepId);
			if (stepIndex !== -1 && state.stepDefinitions[stepIndex].additionalServiceRequest) {
				const serviceRequest = state.stepDefinitions[stepIndex].additionalServiceRequest!;
				const { attributes, sectionIndex } = action.payload;
				serviceRequest.sectionDefinitions[sectionIndex].attributeDefinitions = attributes;
			}
		},
		changeOrderOfSectionsInStepAdditionalServiceRequest: (state, action: PayloadAction<ChangeSectionOrdersInStepAction>) => {
			const stepIndex = state.stepDefinitions.findIndex(x => x.id === action.payload.stepId);
			if (stepIndex !== -1 && state.stepDefinitions[stepIndex].additionalServiceRequest) {
				const serviceRequest = state.stepDefinitions[stepIndex].additionalServiceRequest!;
				const { sections } = action.payload;
				serviceRequest.sectionDefinitions = sections;
			}
		},
		addNewSectionToStepAdditionalServiceRequest: (state, action: PayloadAction<{ stepId: string; sectionName: string }>) => {
			const stepIndex = state.stepDefinitions.findIndex(x => x.id === action.payload.stepId);
			if (stepIndex !== -1 && state.stepDefinitions[stepIndex].additionalServiceRequest) {
				const serviceRequest = state.stepDefinitions[stepIndex].additionalServiceRequest!;
				serviceRequest.sectionDefinitions = [
					...serviceRequest.sectionDefinitions,
					{
						name: action.payload.sectionName,
						isDeleted: false,
						attributeDefinitions: [],
						ServiceRequestId: serviceRequest.id,
					},
				];
			}
		},
		renameSectionInStepAdditionalServiceRequest: (state, action: PayloadAction<RenameSectionInStepAction>) => {
			const stepIndex = state.stepDefinitions.findIndex(x => x.id === action.payload.stepId);
			if (stepIndex !== -1 && state.stepDefinitions[stepIndex].additionalServiceRequest) {
				const serviceRequest = state.stepDefinitions[stepIndex].additionalServiceRequest!;
				const index = serviceRequest.sectionDefinitions.findIndex(x => x.id === action.payload.section.id);
				if (index !== -1) {
					serviceRequest.sectionDefinitions[index] = { ...serviceRequest.sectionDefinitions[index], name: action.payload.newName };
				}
			}
		},
		removeSectionFromStepAdditionalServiceRequest: (state, action: PayloadAction<{ stepId: string; section: SectionDefinition }>) => {
			const stepIndex = state.stepDefinitions.findIndex(x => x.id === action.payload.stepId);
			if (stepIndex !== -1 && state.stepDefinitions[stepIndex].additionalServiceRequest) {
				const serviceRequest = state.stepDefinitions[stepIndex].additionalServiceRequest!;
				const index = serviceRequest.sectionDefinitions.findIndex(x => x.id === action.payload.section.id);
				if (index !== -1) {
					serviceRequest.sectionDefinitions.splice(index, 1);
				}
			}
		},
	},
});

export const {
	setWorkflow,
	resetState,
	setWorkflowId,
	setWorkflowName,
	setWorkflowDescription,
	addStep,
	removeStep,
	changeOrderOfSteps,
	renameStep,
	updateStep,
	addActionToStep,
	removeActionFromStep,
	updateActionInStep,
	updateActionNextStep,
	updateStepPaymentRequired,
	updateStepSLADurationType,
	updateStepSLADuration,
	updateStepAssignType,
	updateStepAbleToSeeCustomerDetails,
	updateStepAbleToSeeOtherStepInformation,
	updateStepAdditionalServiceRequest,
	addFieldToStepAdditionalServiceRequest,
	enableFieldInStepAdditionalServiceRequest,
	removeFieldFromStepAdditionalServiceRequest,
	changeOrderOfAttributesInStepAdditionalServiceRequest,
	changeOrderOfSectionsInStepAdditionalServiceRequest,
	addNewSectionToStepAdditionalServiceRequest,
	renameSectionInStepAdditionalServiceRequest,
	removeSectionFromStepAdditionalServiceRequest,
} = workflowSlice.actions;

export default workflowSlice.reducer;
